import {
  CompanyShare_Create,
  CompanyShare_Update,
  CompanyShareableType,
  deleteV1CompaniesCompanyIdCompanySharesId,
  deleteV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanySharesId,
  getV1CompaniesCompanyIdCompanyShares,
  GetV1CompaniesCompanyIdCompanySharesData,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanyShares,
  GetV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanySharesData,
  patchV1CompaniesCompanyIdCompanySharesId,
  patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanySharesId,
  postV1CompaniesCompanyIdCompanyShares,
  postV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanyShares,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useState } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface GetCompanySharesByCompanyIdParams {
  companyId: string;
  managedCompanyId?: string;
  company_shareable_type?: CompanyShareableType;
  receiver_company_id?: string;
  sender_company_id?: string;
  company_shareable_id?: string;
  between_company_id?: string;
  callBack?: (companyShares: CompanyShare[]) => void;
}

interface CreateCompanyShareByCompanyIdProps extends CompanyShare_Create {
  companyId: string;
  managedCompanyId?: string;
}

interface UpdateCompanyShareProps extends CompanyShare_Update {
  companyShareId: string;
}

export const useCompanyShares = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { companyAssetsStore, userStore } = useStores();

  const getCompanySharesByCompanyId = async (
    options: GetCompanySharesByCompanyIdParams,
  ) => {
    const query:
      | GetV1CompaniesCompanyIdCompanySharesData['query']
      | GetV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanySharesData['query'] =
      {
        'page[limit]': MAX_API_PAGE_SIZE,
      };
    if (options.company_shareable_type) {
      query['filter[company_shareable_type]'] = options.company_shareable_type;
    }
    if (options.receiver_company_id) {
      query['filter[receiver_company_id]'] = options.receiver_company_id;
    }
    if (options.sender_company_id) {
      query['filter[sender_company_id]'] = options.sender_company_id;
    }
    if (options.company_shareable_id) {
      query['filter[company_shareable_id]'] = options.company_shareable_id;
    }
    if (options.between_company_id) {
      query['filter[between_company_id]'] = options.between_company_id;
    }

    try {
      setIsLoading(true);

      const response = options.managedCompanyId
        ? await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanyShares({
            path: {
              'company-id': options.companyId,
              'managed-company-id': options.managedCompanyId,
            },
            query,
          })
        : await getV1CompaniesCompanyIdCompanyShares({
            path: { 'company-id': options.companyId },
            query,
          });

      const parsedCompanyShares = response.data.data.map(CompanyShare.parse);

      companyAssetsStore.setCompanyShares(parsedCompanyShares);
      options.callBack?.(parsedCompanyShares);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.company_shares.failed_to_fetch'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const createCompanyShareByCompanyId = async (
    options: CreateCompanyShareByCompanyIdProps,
  ) => {
    try {
      setIsUpdating(true);

      const body = CompanyShare.deparse(options);
      const response = options.managedCompanyId
        ? await postV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanyShares({
            path: {
              'company-id': options.companyId,
              'managed-company-id': options.managedCompanyId,
            },
            body,
          })
        : await postV1CompaniesCompanyIdCompanyShares({
            path: { 'company-id': options.companyId },
            body,
          });

      const companyShare = CompanyShare.parse(response.data.data);
      companyAssetsStore.addCompanyShare(companyShare);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.company_shares.failed_to_share'),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const updateCompanyShare = async (
    options: UpdateCompanyShareProps,
    companyId?: string,
    managedCompanyId?: string,
  ) => {
    try {
      setIsUpdating(true);
      const effectiveCompanyId = companyId || getEffectiveUserCompanyId(userStore);
      const body = CompanyShare.deparseUpdate(options);

      const response = managedCompanyId
        ? await patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanySharesId({
            path: {
              'company-id': effectiveCompanyId,
              'managed-company-id': managedCompanyId,
              id: options.companyShareId,
            },
            body,
          })
        : await patchV1CompaniesCompanyIdCompanySharesId({
            path: { 'company-id': effectiveCompanyId, id: options.companyShareId },
            body,
          });

      const companyShare = CompanyShare.parse(response.data.data);
      companyAssetsStore.updateCompanyShare(companyShare);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.company_shares.failed_to_update'),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteCompanyShare = async (
    companyShareId: string,
    companyId?: string,
    managedCompanyId?: string,
  ) => {
    try {
      setIsUpdating(true);
      const effectiveCompanyId = companyId || getEffectiveUserCompanyId(userStore);

      managedCompanyId
        ? await deleteV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanySharesId(
            {
              path: {
                'company-id': effectiveCompanyId,
                'managed-company-id': managedCompanyId,
                id: companyShareId,
              },
            },
          )
        : await deleteV1CompaniesCompanyIdCompanySharesId({
            path: {
              'company-id': effectiveCompanyId,
              id: companyShareId,
            },
          });

      companyAssetsStore.deleteCompanyShare(companyShareId);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.company_shares.failed_to_unshare'),
      );
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * Fetches ALL company shares across all pages and stores them in the MobX store.
   * Use this when you need all shares upfront for client-side filtering.
   */
  const getAllCompanyShares = async (options: GetCompanySharesByCompanyIdParams) => {
    const allShares: CompanyShare[] = [];
    let cursor: string | undefined;

    setIsLoading(true);

    try {
      do {
        const query:
          | GetV1CompaniesCompanyIdCompanySharesData['query']
          | GetV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanySharesData['query'] =
          {
            'page[limit]': MAX_API_PAGE_SIZE,
          };

        if (cursor) {
          query['page[after]'] = cursor;
        }

        if (options.company_shareable_type) {
          query['filter[company_shareable_type]'] = options.company_shareable_type;
        }
        if (options.receiver_company_id) {
          query['filter[receiver_company_id]'] = options.receiver_company_id;
        }
        if (options.sender_company_id) {
          query['filter[sender_company_id]'] = options.sender_company_id;
        }
        if (options.company_shareable_id) {
          query['filter[company_shareable_id]'] = options.company_shareable_id;
        }
        if (options.between_company_id) {
          query['filter[between_company_id]'] = options.between_company_id;
        }

        const response = await getV1CompaniesCompanyIdCompanyShares({
          path: { 'company-id': options.companyId },
          query,
        });

        const companyShares = response.data.data.map(CompanyShare.parse);
        allShares.push(...companyShares);

        const pagination = extractPagination(response);
        cursor = pagination.after;
      } while (cursor);

      companyAssetsStore.setCompanyShares(allShares);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.company_shares.failed_to_fetch'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    isUpdating,
    getCompanySharesByCompanyId,
    getAllCompanyShares,
    createCompanyShareByCompanyId,
    updateCompanyShare,
    deleteCompanyShare,
  };
};
